// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Icon, { IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import AlertWarning from "@werner/XDL/Icons/Caution";
import AlertSuccess from "@werner/XDL/Icons/Checkmark";
import Close from "@werner/XDL/Icons/Close";
import AlertInfo from "@werner/XDL/Icons/ContextualHelp";
import AlertError from "@werner/XDL/Icons/OutOfStock";
// eslint-disable-next-line spire/fenced-imports
import { color, getSystemColor, mergeWithCss } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends TypographyProps {
    type?: "success" | "danger" | "warning" | "info";
    showClose?: boolean;
    onClose?: () => void;
    /** if true, you should provide onClose props */
    handleOnCloseExternally?: boolean;
    title?: string;
    customIconSrc?: React.ComponentType | string;
    customIconColor?: string;
}

type Props = OwnProps;

const base64Icons = {
    success: (iconColor: string) =>
        `ata:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='${iconColor}' viewBox='0 0 53 53'%3E%3Cpath d='M41.6 18c-.6-.5-1.5-.5-2.1 0L21.9 35.7l-8-8.1c-.3-.3-.7-.5-1.1-.5-.4 0-.8.2-1.1.5-.3.3-.5.7-.4 1.1 0 .4.2.8.5 1.1l9 8.9c.3.3.7.5 1.1.5h.3c.4 0 .8-.2 1.1-.5l18.5-18.5c.3-.3.4-.7.4-1.1-.1-.4-.2-.8-.6-1.1z'/%3E%3Cpath d='M26.5.6C12.2.6.6 12.2.6 26.5s11.6 25.9 25.9 25.9 25.9-11.6 25.9-25.9S40.8.6 26.5.6zm0 49.2c-12.9 0-23.3-10.5-23.3-23.3S13.6 3.2 26.5 3.2s23.3 10.5 23.3 23.3-10.4 23.3-23.3 23.3z'/%3E%3C/svg%3E`,
    danger: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='${iconColor}' viewBox='0 0 33 33'%3E%3Cpath d='M27.8 5.2c-3-3-7-4.7-11.3-4.7-4.3 0-8.3 1.7-11.3 4.7S.5 12.2.5 16.5c0 4.3 1.7 8.3 4.7 11.3 3.1 3.1 7.2 4.7 11.3 4.7s8.2-1.6 11.3-4.7c3-3 4.7-7 4.7-11.3 0-4.3-1.7-8.3-4.7-11.3zm-2.1 2.1c2.5 2.5 3.8 5.7 3.8 9.2 0 3-1 5.8-2.8 8L8.5 6.3c2.3-1.8 5.1-2.8 8-2.8 3.5 0 6.7 1.4 9.2 3.8zM7.3 25.7c-2.5-2.5-3.8-5.7-3.8-9.2 0-3 1-5.8 2.8-8l18.2 18.2c-2.3 1.8-5.1 2.8-8 2.8-3.5 0-6.7-1.4-9.2-3.8z'/%3E%3C/svg%3E`,
    warning: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='${iconColor}' viewBox='0 0 53 47'%3E%3Cpath d='M25.7 30.9H27c.5 0 .9-.4.9-.9V14.2c0-.5-.4-.9-.9-.9h-1.3c-.5 0-.9.4-.9.9V30c0 .5.4.9.9.9zM26.5 39.4c1.2 0 2.2-1 2.2-2.2 0-1.2-1-2.2-2.2-2.2-1.2 0-2.2 1-2.2 2.2 0 1.2 1 2.2 2.2 2.2z'/%3E%3Cpath d='M51.9 40.9 29.6 2.3C28.9 1.1 27.7.5 26.5.5c-1.3 0-2.6.7-3.1 1.9L1.1 40.9c-.7 1.1-.7 2.5 0 3.6.7 1.2 1.9 1.8 3.1 1.8h44.6c1.2 0 2.4-.7 3.1-1.8.7-1 .7-2.4 0-3.6zM25.8 3.7c.1-.3.5-.4.7-.4.2 0 .5.1.7.3l22.4 38.8c.2.2.2.6 0 .8v.1c-.1.2-.4.4-.8.4H4.2c-.3 0-.6-.2-.8-.5-.2-.2-.2-.6 0-.9L25.8 3.7z'/%3E%3C/svg%3E`,
    info: (iconColor: string) =>
        `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='${iconColor}' viewBox='0 0 30 30'%3E%3Cpath d='m25.4 4.6-.7.7.7-.7C22.6 1.8 18.9.3 15 .3S7.4 1.8 4.6 4.6.3 11.1.3 15s1.5 7.6 4.3 10.4 6.5 4.3 10.4 4.3 7.6-1.5 10.4-4.3 4.3-6.5 4.3-10.4-1.5-7.6-4.3-10.4zM24 24c-2.4 2.4-5.6 3.7-9 3.7S8.4 26.4 6 24s-3.7-5.6-3.7-9S3.6 8.4 6 6s5.6-3.7 9-3.7 6.6 1.3 9 3.7 3.7 5.6 3.7 9-1.3 6.6-3.7 9z'/%3E%3Cpath d='M15 12c.6 0 1 .4 1 1v8c0 .6-.4 1-1 1s-1-.4-1-1v-8c0-.6.4-1 1-1zM15 8c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1z'/%3E%3C/svg%3E`,
};

const getNotifyIcons = (type: OwnProps["type"], useBase64 = true) => {
    const iconColor = getSystemColor(`${type}.main`);
    switch (type) {
        case "success":
            return useBase64 ? (
                base64Icons["success"](iconColor)
            ) : (
                <Icon src={AlertSuccess} color={iconColor} size={20} />
            );
        case "danger":
            return useBase64 ? base64Icons["danger"](iconColor) : <Icon src={AlertError} color={iconColor} size={20} />;
        case "warning":
            return useBase64 ? (
                base64Icons["warning"](iconColor)
            ) : (
                <Icon src={AlertWarning} color={iconColor} size={20} />
            );
        case "info":
            return useBase64 ? base64Icons["info"](iconColor) : <Icon src={AlertInfo} color={iconColor} size={20} />;
    }
};

export const getNotifyStyle = (type: OwnProps["type"], useBase64 = true): TypographyProps => {
    return {
        role: "status",
        "aria-live": "polite",
        "data-test-selector": `notify-${type}`,
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 16px;
            text-align: left;
            border: 1px solid ${color(`${type}.main`)};
            border-left-width: 5px;
            background-color: ${color(`${type}.light`)};
            width: 100%;
            max-width: 100%;
            min-width: 280px;
            margin: 0 0 16px;
            font-size: 15px;
            color: ${color("text.main")};
            letter-spacing: 0;
            line-height: 18px;
            .notify-left-side {
                display: flex;
                align-items: center;
            }
            .notify-icon-wrapper {
                display: flex;
                margin-right: 12px;
            }
            .notify-close-wrapper {
                cursor: pointer;
                margin-left: 18px;
                ${IconWrapper} {
                    color: ${color("common.ui3")};
                    &:hover {
                        color: ${color("text.main")};
                    }
                }
            }
            .notify-content-wrapper {
                display: flex;
                align-items: center;
                flex-wrap: wrap;
                gap: 2px;
            }
            .notify-content-wrapper-title {
                font-weight: 500;
                margin-right: 4px;
            }
            ${useBase64
                ? `
                      padding-left: 40px;
                      &:before {
                          width: 24px;
                          height: 24px;
                          min-width: 24px;
                          position: absolute;
                          left: 8px;
                          content: " ";
                          background-repeat: no-repeat;
                          transform: scale(0.75);
                          background-image: url("${getNotifyIcons(type, useBase64)}");
                      }
                  `
                : ""}
        `,
    };
};

const Notify: FC<Props> = ({
    type,
    children,
    css: extendedCssStyle,
    showClose = false,
    onClose,
    handleOnCloseExternally,
    title,
    customIconSrc,
    customIconColor,
}) => {
    const [styles] = React.useState(() => mergeWithCss(getNotifyStyle(type, false), { css: extendedCssStyle }));
    const [visible, setVisibility] = React.useState(true);

    if (!React.Children.count(children) || !visible) {
        return null;
    }

    const handleCloseNotify = () => {
        if (handleOnCloseExternally) {
            onClose?.();
            return;
        }
        setVisibility(false);
        onClose?.();
    };
    return (
        <Typography {...styles}>
            <Typography className="notify-left-side">
                <Typography className="notify-icon-wrapper">
                    {customIconSrc ? (
                        <Icon src={customIconSrc} color={customIconColor} size={20} />
                    ) : getNotifyIcons(type, false)}
                </Typography>
                <Typography className="notify-content-wrapper">
                    {title && <Typography className="notify-content-wrapper-title">{title}:</Typography>}
                    {children}
                </Typography>
            </Typography>
            {showClose && (
                <Typography
                    className="notify-close-wrapper"
                    onClick={handleCloseNotify}
                    role="button"
                    aria-label="Close"
                >
                    <Icon src={Close} size={10} color="common.ui3" />
                </Typography>
            )}
        </Typography>
    );
};

export default Notify;
